"use client"

import type { Photo } from "@/lib/photos"
import { Download, Calendar, Camera, Heart, ImageIcon, ImageOff, ShoppingCart } from "lucide-react"
import { useCallback, useState } from "react"
import { useSelection } from "@/context/selection-context"
import { useCart } from "@/context/cart-context"
import { useSettings } from "@/components/branding-provider"
import { cn } from "@/lib/utils"
import { getWatermarkedDownloadUrl } from "@/lib/lightbox-image"
import { getDefaultPrintProduct, isPrintProductSafeForPhoto } from "@/lib/print-catalog"
import { getThumbnailSrcSet, PHOTO_GRID_SIZES } from "@/lib/image-url"

const RETRY_DELAY_MS = 800

function withRetryParam(url: string) {
  return `${url}${url.includes('?') ? '&' : '?'}retry=1`
}

interface PhotoCardProps {
  photo: Photo
  onClick?: () => void
  priority?: boolean
  ecommerceEnabled?: boolean
  favoritesEnabled?: boolean
  freeDownloadMaxWidth?: number
  downloadVersion?: string
  freeDownloadsEnabled?: boolean
  shareToken?: string
  /** Saved favorites-list token that opens this private photo. */
  favoriteToken?: string
}

export function PhotoCard({ photo, onClick, priority = false, ecommerceEnabled = true, favoritesEnabled = true, freeDownloadMaxWidth = 1200, downloadVersion = 'default', freeDownloadsEnabled = true, shareToken, favoriteToken }: PhotoCardProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [imageFailed, setImageFailed] = useState(false)
  const thumbnailSrc = photo.thumbnailSrc || photo.src
  const imageSrc = loadAttempt === 0 ? thumbnailSrc : withRetryParam(thumbnailSrc)
  const imageSrcSet = loadAttempt === 0 ? getThumbnailSrcSet(photo.thumbnailSrc) : undefined

  // An image can finish loading before React hydrates and attaches onLoad.
  const handleImageRef = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth > 0) setImageLoaded(true)
  }, [])

  const handleImageError = () => {
    if (loadAttempt === 0) {
      window.setTimeout(() => setLoadAttempt(1), RETRY_DELAY_MS)
    } else {
      setImageFailed(true)
    }
  }
  const { isSelected, toggleSelection } = useSelection()
  const { addToCart, addPrintToCart, isInCart, isPrintInCart } = useCart()
  const settings = useSettings()
  const selected = isSelected(photo.id)
  const inCart = isInCart(photo.id)
  const defaultPrintProduct = getDefaultPrintProduct(settings.commerce?.prints)
  const printsEnabled = ecommerceEnabled
    && Boolean(settings.commerce?.prints?.enabled && defaultPrintProduct)
    && (defaultPrintProduct ? isPrintProductSafeForPhoto(photo, defaultPrintProduct) : false)
  const inPrintCart = defaultPrintProduct ? isPrintInCart(photo.id, defaultPrintProduct.id) : false

  const handleSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleSelection(photo, { shareToken })
  }

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation()
    addToCart(photo)
  }

  const handleAddPrintToCart = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (defaultPrintProduct) addPrintToCart(photo, defaultPrintProduct.id)
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering the card click
    setIsDownloading(true)
    const anchor = document.createElement("a")
    anchor.href = getWatermarkedDownloadUrl(photo, { freeDownloadMaxWidth, downloadVersion, shareToken, favoriteToken })
    anchor.download = `watermarked-${photo.filename.split('/').pop() || 'photo.jpg'}`
    anchor.style.display = "none"
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.setTimeout(() => setIsDownloading(false), 750)
  }

  return (
    <div
      className={cn(
        "group relative aspect-[3/2] w-full cursor-pointer overflow-hidden rounded-2xl border bg-muted shadow-sm transition-all duration-300",
        selected
          ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
          : "hover:-translate-y-0.5 hover:shadow-lg"
      )}
      onClick={onClick}
    >
      {/* Placeholder stays visible underneath until the photo paints over it. */}
      {photo.blurDataURL ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photo.blurDataURL}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top blur-md"
        />
      ) : !imageLoaded && !imageFailed ? (
        // Static, not pulsing: a grid of pulsing tiles reads as flicker while photos load.
        <div aria-hidden className="absolute inset-0 bg-muted-foreground/10" />
      ) : null}

      {imageFailed ? (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/80" role="img" aria-label="Photo unavailable">
          <ImageOff className="h-8 w-8 text-muted-foreground/60" />
        </div>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={imageSrc}
          ref={handleImageRef}
          src={imageSrc}
          srcSet={imageSrcSet}
          sizes={imageSrcSet ? PHOTO_GRID_SIZES : undefined}
          alt={photo.filename}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={handleImageError}
          className={cn(
            "absolute inset-0 h-full w-full object-cover object-top transition-all duration-500 will-change-transform",
            selected ? "scale-[1.03]" : "group-hover:scale-[1.03]",
            // Above-the-fold photos paint progressively with no fade; the rest fade in over the placeholder.
            priority || imageLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}

      {/* Overlay Gradient */}
      <div className={cn(
        "absolute inset-0 bg-black/0 transition-colors duration-300",
        selected && "bg-black/10"
      )} />

      <div className={cn(
        "absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/75 to-transparent transition-opacity duration-300",
        selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
      )} />

      {/* Favorite Button - Top Left */}
      {favoritesEnabled && (
        <button
          onClick={handleSelection}
          className={cn(
            "absolute left-3 top-3 z-10 flex min-h-10 min-w-10 items-center justify-center rounded-full border border-white/15 transition-all duration-300 backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
            selected
              ? "bg-white text-red-500 shadow-lg opacity-100"
              : "bg-black/35 text-white opacity-100 hover:bg-white hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100"
          )}
          title={selected ? "Remove from favorites" : "Add to favorites"}
          aria-label={selected ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart className={cn("h-5 w-5", selected && "fill-current")} />
        </button>
      )}

      {/* Action Buttons - Bottom Right */}
      <div className={cn(
        "absolute bottom-3 right-3 z-10 flex items-center gap-2 transition-opacity duration-300",
        inCart ? "opacity-100" : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
      )}>
        {/* Add digital download */}
        {ecommerceEnabled && (
          <button
            onClick={handleAddToCart}
            className={cn(
              "flex min-h-10 min-w-10 items-center justify-center rounded-full border border-white/15 transition-all duration-300 backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
              inCart
                ? "bg-white text-green-600 shadow-lg"
                : "bg-black/35 text-white hover:bg-white hover:text-green-600"
            )}
            title={inCart ? "Digital download in cart" : "Add digital download to cart"}
            aria-label={inCart ? "Digital download in cart" : "Add digital download to cart"}
          >
            <ShoppingCart className={cn("h-5 w-5", inCart && "fill-current")} />
          </button>
        )}

        {printsEnabled && (
          <button
            onClick={handleAddPrintToCart}
            className={cn(
              "flex min-h-10 min-w-10 items-center justify-center rounded-full border border-white/15 transition-all duration-300 backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
              inPrintCart
                ? "bg-white text-amber-700 shadow-lg"
                : "bg-black/35 text-white hover:bg-white hover:text-amber-700"
            )}
            title={inPrintCart ? "Print in cart" : "Order print"}
            aria-label={inPrintCart ? "Print in cart" : "Order print"}
          >
            <ImageIcon className={cn("h-5 w-5", inPrintCart && "fill-current")} />
          </button>
        )}

        {/* Download Button */}
        {freeDownloadsEnabled && (
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className={cn(
              "flex min-h-10 min-w-10 items-center justify-center rounded-full border border-white/15 transition-all duration-300 backdrop-blur-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
              "bg-black/35 text-white hover:bg-white hover:text-primary disabled:opacity-50"
            )}
            title="Download Watermarked"
            aria-label="Download watermarked image"
          >
            <Download className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Bottom Content */}
      <div className={cn(
        "absolute bottom-0 left-0 right-0 p-4 transition-all duration-300",
        selected ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 sm:group-hover:opacity-100 sm:group-hover:translate-y-0"
      )}>
        <div className="flex flex-col gap-1 text-white">
          <div className="flex items-center gap-2 text-xs font-medium text-white/90">
            <Calendar className="h-3.5 w-3.5" />
            <span>{new Date(photo.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/70">
            <Camera className="h-3.5 w-3.5" />
            <span className="truncate max-w-[200px]">{photo.filename.split('/').pop()}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
